import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { COMPANY, SERVICES } from "./data";
import { Reveal, SectionHead } from "./Reveal";
import { EASE, requestOffer } from "./scroll";
import { Pic } from "./Pic";
import { Link } from "react-router-dom";
import shared from "@/seo/shared";

const ServiceCard = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <motion.article
      className="group relative flex min-h-[440px] flex-col overflow-hidden bg-paper p-7 sm:p-8"
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease: EASE, delay: (i % 4) * 0.08 }}
      data-testid={`service-card-${s.id}`}
    >
      <Pic name={s.img} alt={`${s.title} – Christian Bedachungen, Dachdecker in Schifferstadt`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-0 transition-[opacity,transform] duration-700 ease-out group-hover:scale-100 group-hover:opacity-100" />
      <div className="absolute inset-0 bg-ink/85 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="relative flex h-full flex-1 flex-col">
        <div className="flex items-center justify-between">
          <span className="grid h-14 w-14 place-items-center rounded-full border border-ink/15 text-terra-ink transition-colors duration-500 group-hover:border-terra group-hover:bg-terra-deep group-hover:text-white">
            <Icon size={22} strokeWidth={1.6} />
          </span>
          <span className="font-display text-lg font-semibold text-ink/50 transition-colors duration-500 group-hover:text-white/70">0{i + 1}</span>
        </div>
        <h3 className="mt-9 text-xs font-bold uppercase tracking-[0.2em] text-terra-ink transition-colors duration-500 group-hover:text-terra">{s.title}</h3>
        <p className="mt-3 font-display text-[1.9rem] font-bold uppercase leading-[0.98] text-ink transition-colors duration-500 group-hover:text-white">{s.benefit}</p>
        <p className="mt-4 flex-1 text-[0.95rem] leading-relaxed text-slate-700 transition-colors duration-500 group-hover:text-slate-200">{s.desc}</p>
        <button type="button" onClick={() => requestOffer(s.option)} className="btn-primary mt-7 w-full justify-center whitespace-nowrap !gap-2 !px-3 !py-4 !text-[0.7rem] !tracking-[0.03em]" data-testid={`service-card-cta-${s.id}`}>
          Kostenlose Beratung anfordern <ArrowRight size={16} className="btn-arrow shrink-0" />
        </button>
        <Link to={`/${shared.services.find((x) => x.sid === s.id)?.slug}`} className="btn-dark mt-3 w-full justify-center !border-ink/80 !px-3 !py-3.5 !text-[0.72rem] group-hover:!border-white group-hover:!text-white" data-testid={`service-card-more-${s.id}`}>
          Mehr erfahren <ArrowRight size={16} className="btn-arrow" />
        </Link>
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="leistungen" aria-labelledby="leistungen-title" className="relative bg-paper py-24 text-ink sm:py-32 lg:py-40" data-testid="services-section">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <SectionHead id="leistungen-title" tone="light" eyebrow="Leistungen" title={<>Alles rund<br />ums Dach.</>} />
        <Reveal className="max-w-md lg:pb-3" delay={0.15}>
          <p className="text-base leading-relaxed text-slate-700 sm:text-lg">
            Von der ersten Inspektion bis zur letzten Firstkappe: Wir übernehmen alle Arbeiten am Steil- und <Link to="/flachdachabdichtung-schifferstadt" className="font-semibold text-terra-ink underline underline-offset-4" data-testid="services-flachdach-link">Flachdach</Link> – mit eigenem Team, aus einer Hand und zum Festpreis.
          </p>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-px border border-ink/10 bg-ink/10 sm:mt-20 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.filter((s) => s.home !== false).map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
      </div>
      <Reveal className="mt-12 flex flex-col items-start justify-between gap-6 bg-ink p-8 text-white sm:p-10 lg:flex-row lg:items-center">
        <div>
          <p className="font-display text-3xl font-bold uppercase leading-none sm:text-4xl">Nicht sicher, was Ihr Dach braucht?</p>
          <p className="mt-3 text-slate-300">Wir schauen es uns an – kostenlos, innerhalb von 48 Stunden, mit Festpreisangebot.</p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          <button type="button" onClick={() => requestOffer("Dachcheck & Beratung")} className="btn-primary btn-mega justify-center" data-testid="services-band-cta">Gratis-Dachcheck sichern <ArrowRight size={18} className="btn-arrow" /></button>
          <a href={COMPANY.phoneHref} className="btn-ghost btn-mega justify-center" data-testid="services-band-phone"><Phone size={18} /> {COMPANY.phoneDisplay}</a>
        </div>
      </Reveal>
    </div>
  </section>
);
